import { Injectable } from '@angular/core';
import { AuthService } from '../authentication/auth.service';
import { Theme } from 'src/app/hickory-api';
import { StorageHelper } from '../storage/storage-helper';

export interface Link {
  id: string;
  url: string;
  requirePartnerVariant?: boolean;
  variants?: LinkVariant[];
}

export interface LinkVariant {
  brand?: string;
  url?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LinksService {
  private readonly themes = new Map<string, Theme>();
  private readonly links = new Map<string, Link>();

  private readonly directoryManagementOverviewLink: Link = {
    id: 'directory-management-overview', 
    url: 'https://support.konexus.com/directory-management-overview', 
    requirePartnerVariant: true,
    variants: 
    [ 
      { 
        brand: 'civicready', 
        url: 'https://www.civicready.civicplus.help/hc/en-us/articles/13487204737431-Directory-Management', 
      } 
    ], 
  };

  private readonly adminManagementViewLink: Link = {
    id: 'admin-management-overview', 
    url: 'https://support.konexus.com/administrator-passwords-view', 
    requirePartnerVariant: true,
    variants: 
    [ 
      { 
        brand: 'civicready', 
        url: 'https://www.civicready.civicplus.help/hc/en-us/articles/4404582427543-Add-Manage-Administrators', 
      } 
    ], 
  };

  private readonly smsEmailStatusLink: Link = {
    id: 'sms-email-status', 
    url: 'https://support.konexus.com/email-status', 
    requirePartnerVariant: true,
    variants: 
    [ 
      { 
        brand: 'civicready', 
        url: 'https://www.civicready.civicplus.help/hc/en-us/articles/16194692420759-Email-and-SMS-Text-Status-Indicators', 
      } 
    ],
  };

  private readonly voiceStatusLink: Link = {
    id: 'voice-status', 
    url: 'https://support.konexus.com/what-do-the-call-status-indicators-mean', 
    requirePartnerVariant: true,
    variants: 
    [ 
      { 
        brand: 'civicready', 
        url: 'https://www.civicready.civicplus.help/hc/en-us/articles/14691505243159-Call-Status-Indicators', 
      } 
    ],
  };

  private readonly socialMediaAddAccountLink: Link = {
    id: 'social-media-add-account',
    url: 'https://support.konexus.com/how-to-add-social-media-accounts',
    requirePartnerVariant: true,
    variants: 
    [ 
      { 
        brand: 'civicready', 
        url: 'https://www.civicready.civicplus.help/hc/en-us/articles/4405571625879-Add-Manage-Social-Media-Accounts', 
      } 
    ],
  };

  public readonly TEAMS_HIGH_IMPORTANCE = 'teams-high-importance';
  private readonly teamsHighImportanceLink: Link = {
    id: this.TEAMS_HIGH_IMPORTANCE,
    url: 'https://support.konexus.com/microsoft-teams-high-importance-messages',
    requirePartnerVariant: true,
    // variants: 
    // [ 
    //   { 
    //     brand: 'civicready', 
    //     url: '', 
    //   } 
    // ],
  };

  public readonly TEAMS_CONFIG_OVERVIEW = 'teams-config-overview';
  private readonly teamsConfigOverviewLink: Link = {
    id: this.TEAMS_CONFIG_OVERVIEW,
    url: 'https://support.konexus.com/microsoft-teams-integration-overview',
    requirePartnerVariant: true,
    // variants: 
    // [ 
    //   { 
    //     brand: 'civicready', 
    //     url: '', 
    //   } 
    // ],
  };

  constructor(
    private readonly authService: AuthService,
  ) { 
    this.links.set('directory-management-overview', this.directoryManagementOverviewLink);
    this.links.set('admin-management-overview', this.adminManagementViewLink);
    this.links.set('sms-email-status', this.smsEmailStatusLink);
    this.links.set('voice-status', this.voiceStatusLink);
    this.links.set('social-media-add-account', this.socialMediaAddAccountLink);
    this.links.set(this.TEAMS_HIGH_IMPORTANCE, this.teamsHighImportanceLink);
    this.links.set(this.TEAMS_CONFIG_OVERVIEW, this.teamsConfigOverviewLink);
  }

  private getBrand(): string {
    return this.getTheme()?.brandName?.toLowerCase();
  }

  private getTheme(): Theme {
    const key = this.authService.user.snapshot?.id?.toString();
    let theme = this.themes.get(key);
    if (!theme) {
      theme = StorageHelper.getTheme();
      this.themes.set(key, theme);
    }
    return theme;
  }

  resolveLinkUrl(id: string): string {
    const link = this.links.get(id);
    if (!link?.url) return null;
    const variant = this.findVariant(link);
    if (link.requirePartnerVariant && this.isParterBrand()) {
      return variant?.url;
    } else {
      return variant?.url ?? link.url;
    }
  }

  private isParterBrand(): boolean {
    const brand = this.getBrand();
    return brand !== 'alertsense' && brand !== 'konexus';
  }

  private findVariant(link: Link): LinkVariant {
    const brand = this.getBrand();
    return link?.variants?.find(v => v.brand === brand)
  }
}
